import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle, Circle, ShieldStar } from "@phosphor-icons/react";

// Reusable stateful lifecycle checklist. type: "onboarding" | "offboarding".
// When `rid` is set, operates in ClaroConsole (super-admin) mode.
export default function LifecycleChecklist({ type, rid = null, title, blurb, onProgress }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const base = rid ? `/console/lifecycle/${rid}/${type}` : `/lifecycle/${type}`;

  const load = useCallback(() => {
    api.get(base).then((r) => { setData(r.data); onProgress && onProgress(r.data); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [base]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const toggle = async (item) => {
    if (!item.can_check) return;
    setBusy(item.key);
    try {
      const url = rid ? `${base}/${item.key}` : `/lifecycle/${type}/${item.key}`;
      const r = await api.post(url, { done: !item.done, note: item.note || "" });
      setData(r.data); onProgress && onProgress(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!data) return <div className="text-sm text-muted-foreground p-4" data-testid={`lifecycle-loading-${type}`}>Loading…</div>;

  return (
    <div data-testid={`lifecycle-${type}`}>
      {(title || blurb) && (
        <div className="mb-4">
          {title && <h3 className="text-lg font-bold text-foreground">{title}</h3>}
          {blurb && <p className="text-sm text-muted-foreground mt-1">{blurb}</p>}
        </div>
      )}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 h-2 bg-muted overflow-hidden">
          <div className="h-full bg-[#128C77] transition-all" style={{ width: `${data.percent}%` }} data-testid={`lifecycle-progress-${type}`} />
        </div>
        <span className="text-sm font-bold text-[#071B3E]">{data.percent}%</span>
        <span className="text-xs text-muted-foreground">{data.done}/{data.total}</span>
      </div>
      <ul className="space-y-2">
        {data.items.map((it) => (
          <li key={it.key} data-testid={`lifecycle-item-${it.key}`}
            className={`flex items-start gap-3 border p-3 ${it.done ? "border-emerald-200 bg-emerald-50/40" : "border-border bg-card"}`}>
            <button disabled={!it.can_check || busy === it.key} onClick={() => toggle(it)}
              data-testid={`lifecycle-toggle-${it.key}`}
              className={`mt-0.5 shrink-0 ${it.can_check ? "cursor-pointer" : "cursor-not-allowed opacity-70"}`}
              aria-label={it.done ? "Mark incomplete" : "Mark complete"}>
              {it.done ? <CheckCircle size={22} weight="fill" className="text-emerald-600" />
                       : <Circle size={22} className="text-muted-foreground" />}
            </button>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[13px] font-semibold text-foreground">{it.title}</span>
                {it.owner === "claro" && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 bg-[#071B3E]/10 text-[#071B3E]">
                    <ShieldStar size={11} weight="fill" /> ClaroCompliance
                  </span>
                )}
                {it.auto && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 bg-sky-50 text-sky-700"
                    data-testid={`lifecycle-auto-${it.key}`}>
                    Auto-detected
                  </span>
                )}
              </div>
              <div className="text-[12px] text-muted-foreground leading-snug">{it.desc}</div>
              {it.done && it.done_by && (
                <div className="text-[11px] text-emerald-700 mt-1">✓ {it.done_by} · {it.done_at ? new Date(it.done_at).toLocaleDateString() : ""}</div>
              )}
              {it.note && <div className="text-[11px] text-muted-foreground mt-0.5 italic">“{it.note}”</div>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
